import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Text,
  Loader,
  Center,
  Box,
  ActionIcon,
  Group,
  Progress,
  Stack,
  Button,
  Badge,
  Paper,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconX,
  IconVolume,
  IconHelpCircle,
  IconTrophy,
  IconSparkles,
} from '@tabler/icons-react';
import { useCards, useStartSession, useRecordAnswer, useCompleteSession } from '@/features/flashcards/hooks/useFlashcards';
import { usePlatform } from '@/hooks/usePlatform';
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat';
import { flashcardApi } from '@/features/flashcards/api/flashcardApi';
import { ContentRenderer, hasAudio, getAudioUrl } from '@/features/flashcards/utils/contentFormatters';
import type { Flashcard, StudySession } from '@/features/flashcards/types/flashcard';
import './MobileStudyPage.css';

/**
 * Mobile Study Page - Touch-Optimized Flashcard Study
 *
 * Features:
 * - Tap to flip cards with smooth 3D animation
 * - Swipe left = Hard/Incorrect
 * - Swipe right = Easy/Correct
 * - Swipe up/down = Flip card
 * - Full-screen immersive study mode
 * - Progress tracking
 */
export function MobileStudyPage() {
  const { deckId } = useParams<{ deckId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isIOS } = usePlatform();

  const mode = searchParams.get('mode') || 'all';
  const { data: allCards, isLoading } = useCards(deckId);
  const createSession = useStartSession();
  const recordAnswer = useRecordAnswer();
  const completeSession = useCompleteSession();

  const [sessionId, setSessionId] = useState<string>('');

  const heartbeatFn = useCallback(
    (id: string) => flashcardApi.sessionHeartbeat(id),
    [],
  );
  useSessionHeartbeat(
    sessionId || null,
    '/flashcards/study/sessions/{id}/heartbeat',
    heartbeatFn,
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [answeredCards, setAnsweredCards] = useState(new Set<string>());
  const [isComplete, setIsComplete] = useState(false);
  const [completedSession, setCompletedSession] = useState<StudySession | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Filter cards based on mode
  const cards = allCards?.filter((card: Flashcard) => {
    if (mode === 'due') {
      return card.next_review_at && new Date(card.next_review_at) <= new Date();
    }
    return true;
  }) || [];

  const currentCard = cards[currentIndex];
  const progress = cards.length > 0 ? ((currentIndex + 1) / cards.length) * 100 : 0;

  // Start session when cards load
  useEffect(() => {
    if (cards.length > 0 && !sessionId && deckId) {
      createSession.mutate({ deck_id: deckId, mode: mode as 'all' | 'due' }, {
        onSuccess: (session) => {
          setSessionId(session.id);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards.length, sessionId, deckId, mode]);

  // Minimum swipe distance (in px) to trigger action
  const minSwipeDistance = 70;
  const minFlipSwipeDistance = 50;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchEnd(null);
    setIsDragging(false);
    setDragOffset({ x: 0, y: 0 });
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;

    setTouchEnd({ x: touch.clientX, y: touch.clientY });

    // Only set dragging if moved more than 5px
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      setIsDragging(true);
    }

    // Update drag offset for visual feedback
    if (isFlipped) {
      // When flipped, allow horizontal dragging for answer
      setDragOffset({ x: deltaX, y: 0 });
    } else {
      // When not flipped, show vertical drag for flip hint
      setDragOffset({ x: 0, y: deltaY });
    }
  }, [touchStart, isFlipped]);

  const handleAnswer = useCallback((isCorrect: boolean, difficulty: 'easy' | 'medium' | 'hard' = 'medium') => {
    if (!currentCard || !sessionId || answeredCards.has(currentCard.id)) return;

    // Record the answer
    recordAnswer.mutate({
      cardId: currentCard.id,
      data: {
        session_id: sessionId,
        is_correct: isCorrect,
        difficulty,
      },
    });

    setAnsweredCards(prev => new Set([...prev, currentCard.id]));

    // Move to next card or complete
    if (currentIndex < cards.length - 1) {
      // Start transition - animate card sliding out
      setIsTransitioning(true);
      const slideOutDistance = isCorrect ? 400 : -400;
      setDragOffset({ x: slideOutDistance, y: 0 });

      setTimeout(() => {
        // Change to next card and reset position instantly
        setCurrentIndex(prev => prev + 1);
        setIsFlipped(false);
        setIsTransitioning(false);
        setDragOffset({ x: 0, y: 0 });
      }, 300);
    } else {
      // Study session complete - call completeSession to trigger assignment completion
      completeSession.mutate(sessionId, {
        onSuccess: (session) => {
          setCompletedSession(session);
          setIsComplete(true);
        },
        onError: () => {
          // Still show completion even if API call fails
          setIsComplete(true);
        },
      });
    }
  }, [currentCard, sessionId, answeredCards, currentIndex, cards.length, recordAnswer, completeSession]);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) {
      setTouchStart(null);
      setDragOffset({ x: 0, y: 0 });
      setIsDragging(false);
      return;
    }

    const deltaX = touchEnd.x - touchStart.x;
    const deltaY = touchEnd.y - touchStart.y;

    // Reset drag state
    setDragOffset({ x: 0, y: 0 });
    setIsDragging(false);

    if (isFlipped) {
      // Horizontal swipes for answering when card is flipped
      if (Math.abs(deltaX) > minSwipeDistance) {
        // Haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }

        if (deltaX > 0) {
          // Swipe right = Easy/Correct
          handleAnswer(true, 'easy');
        } else {
          // Swipe left = Hard/Incorrect
          handleAnswer(false, 'hard');
        }
      }
    } else {
      // Vertical swipes for flipping when card is not flipped
      if (Math.abs(deltaY) > minFlipSwipeDistance) {
        setIsFlipped(true);
        // Haptic feedback
        if (navigator.vibrate) {
          navigator.vibrate(30);
        }
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
  }, [touchStart, touchEnd, isFlipped, handleAnswer]);

  const handleCardTap = useCallback(() => {
    if (isDragging) return; // Don't flip if user was dragging
    setIsFlipped(!isFlipped);
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
  }, [isFlipped, isDragging]);

  const handleBack = () => {
    navigate('/flashcards');
  };

  const playAudio = useCallback((url: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const audio = new Audio(url);
    audio.play().catch((err) => console.error('Audio playback failed:', err));
  }, []);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!currentCard || cards.length === 0) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text size="lg" fw={500}>No cards to study</Text>
          <Button onClick={handleBack}>Back to Decks</Button>
        </Stack>
      </Center>
    );
  }

  // Show completion screen when study session is complete
  if (isComplete) {
    const assignmentCompleted = completedSession?.assignment_completed ?? false;
    const completionType = completedSession?.completion_type;
    const metadata = completedSession?.completion_metadata;

    return (
      <Center h="100vh" p="md">
        <Paper p="xl" withBorder ta="center" maw={400}>
          <Stack gap="lg" align="center">
            {assignmentCompleted ? (
              <IconSparkles size={64} stroke={1.5} color="#fab005" />
            ) : (
              <IconTrophy size={64} stroke={1.5} color="#12b886" />
            )}
            <Box>
              <Text size="xl" fw={700}>
                {assignmentCompleted ? 'Assignment Completed!' : 'Session Complete!'}
              </Text>
              {assignmentCompleted && completionType && (
                <Badge size="lg" color="yellow" mt="sm">
                  {completionType === 'quick' ? 'Quick Completion' : 'Mastery Achieved'}
                </Badge>
              )}
              <Text c="dimmed" mt="sm">
                {assignmentCompleted
                  ? 'Congratulations! You have completed this assignment.'
                  : 'Great work on completing the deck!'}
              </Text>

              {metadata && (
                <Stack gap="xs" mt="md">
                  {metadata.accuracy !== undefined && (
                    <Group justify="center" gap="xs">
                      <Text size="sm" fw={500}>Accuracy:</Text>
                      <Text size="sm">{Math.round(metadata.accuracy * 100)}%</Text>
                    </Group>
                  )}
                  {metadata.mastery_percentage !== undefined && (
                    <>
                      <Group justify="center" gap="xs">
                        <Text size="sm" fw={500}>Mastery:</Text>
                        <Text size="sm">{Math.round(metadata.mastery_percentage)}%</Text>
                      </Group>
                      <Progress
                        value={metadata.mastery_percentage}
                        color="teal"
                        size="lg"
                        radius="xl"
                        mt="xs"
                      />
                    </>
                  )}
                </Stack>
              )}
            </Box>
            <Group>
              <Button variant="light" onClick={handleBack}>
                Back to Decks
              </Button>
              <Button variant="light" onClick={() => window.location.reload()}>
                Study Again
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Center>
    );
  }

  const frontHasAudio = hasAudio(currentCard.front);
  const frontAudioUrl = getAudioUrl(currentCard.front);

  // Calculate rotation and opacity based on drag
  const dragProgress = Math.min(Math.abs(dragOffset.x) / 150, 1);
  const rotation = (dragOffset.x / 150) * 15; // Max 15 degrees rotation
  const correctOpacity = dragOffset.x > 0 ? dragProgress : 0;
  const incorrectOpacity = dragOffset.x < 0 ? dragProgress : 0;

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f9fa',
      }}
    >
      {/* Top Bar */}
      <Box
        style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          paddingTop: isIOS ? 'env(safe-area-inset-top)' : 0,
        }}
      >
        <Group justify="space-between" p="md" gap="xs">
          <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleBack}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Text size="sm" fw={500}>
            {currentIndex + 1} / {cards.length}
          </Text>
          <Box w={40} /> {/* Spacer for centering */}
        </Group>
        <Progress value={progress} size="xs" color="teal" />
      </Box>

      {/* Flashcard Area */}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          position: 'relative',
        }}
      >
        {/* Next card preview (shows card underneath) */}
        {currentIndex < cards.length - 1 && (
          <Box
            className="mobile-flashcard-container"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) scale(0.95)',
              opacity: 0.4,
              pointerEvents: 'none',
              zIndex: 0,
              filter: 'grayscale(100%)',
            }}
          >
            <Paper shadow="md" p="xl" withBorder style={{ height: '100%', backgroundColor: 'white' }}>
              <Stack gap="md" align="center" justify="space-between" h="100%" style={{ userSelect: 'none' }}>
                <Badge size="md" variant="light" color="blue">
                  Front
                </Badge>

                <Stack gap="md" align="center" style={{ flex: 1, justifyContent: 'center' }}>
                  <ContentRenderer content={cards[currentIndex + 1].front} />
                </Stack>

                <Text size="sm" c="dimmed">
                  Next card
                </Text>
              </Stack>
            </Paper>
          </Box>
        )}

        {/* Swipe Indicators */}
        {isFlipped && (
          <>
            <Box
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: incorrectOpacity,
                transition: 'opacity 0.1s',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <Stack align="center" gap={4}>
                <IconX size={48} color="#fa5252" stroke={2.5} />
                <Text fw={700} c="red" size="lg">HARD</Text>
              </Stack>
            </Box>
            <Box
              style={{
                position: 'absolute',
                right: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: correctOpacity,
                transition: 'opacity 0.1s',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <Stack align="center" gap={4}>
                <IconCheck size={48} color="#12b886" stroke={2.5} />
                <Text fw={700} c="teal" size="lg">EASY</Text>
              </Stack>
            </Box>
          </>
        )}

        {/* Current Flashcard */}
        <Box
          key={currentCard.id}
          className="mobile-flashcard-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={handleCardTap}
          style={{
            transform: isTransitioning
              ? `translateX(${dragOffset.x}px) translateY(${dragOffset.y}px) rotateZ(${rotation}deg)`
              : `translateX(${dragOffset.x}px) translateY(${dragOffset.y}px) rotateZ(${rotation}deg)`,
            transition: isDragging ? 'none' : (isTransitioning ? 'transform 0.3s ease-out' : 'none'),
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box className={`mobile-flashcard ${isFlipped ? 'flipped' : ''}`}>
            {/* Front */}
            <Paper className="mobile-flashcard-face mobile-flashcard-front" shadow="lg" p="xl" withBorder>
              <Stack gap="md" align="center" justify="space-between" h="100%" style={{ userSelect: 'none' }}>
                <Badge size="md" variant="light" color="blue">
                  Front
                </Badge>

                <Stack gap="md" align="center" style={{ flex: 1, justifyContent: 'center' }}>
                  <ContentRenderer content={currentCard.front} />

                  {/* Phonetic + Speaker for dictionary entries */}
                  {currentCard.back.content_type === 'dictionary_entry' && currentCard.back.content_metadata && (
                    <>
                      {currentCard.back.content_metadata.phonetic && (
                        <Text fw={600} c="dimmed" fz={24}>
                          {currentCard.back.content_metadata.phonetic}
                        </Text>
                      )}
                      {currentCard.back.content_metadata.audio_url && (
                        <ActionIcon
                          variant="filled"
                          color="blue"
                          size="xl"
                          radius="xl"
                          onClick={playAudio(currentCard.back.content_metadata.audio_url)}
                        >
                          <IconVolume size={24} />
                        </ActionIcon>
                      )}
                    </>
                  )}

                  {/* Audio for non-dictionary entries */}
                  {currentCard.front.content_type !== 'dictionary_entry' && frontHasAudio && frontAudioUrl && (
                    <ActionIcon
                      variant="light"
                      color="blue"
                      size="xl"
                      radius="xl"
                      onClick={playAudio(frontAudioUrl)}
                    >
                      <IconVolume size={24} />
                    </ActionIcon>
                  )}

                  {/* Hint */}
                  {currentCard.front.hint && (
                    <Group gap={4}>
                      <IconHelpCircle size={16} />
                      <Text size="sm" c="dimmed" ta="center">
                        {currentCard.front.hint}
                      </Text>
                    </Group>
                  )}
                </Stack>

                <Text size="sm" c="dimmed">
                  Tap or swipe up to flip
                </Text>
              </Stack>
            </Paper>

            {/* Back */}
            <Paper className="mobile-flashcard-face mobile-flashcard-back" shadow="lg" p="xl" withBorder>
              <Stack gap="md" align="center" justify="space-between" h="100%" style={{ userSelect: 'none' }}>
                <Badge size="md" variant="light" color="teal">
                  Back
                </Badge>

                <Box style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', overflow: 'auto' }}>
                  <ContentRenderer content={currentCard.back} />
                </Box>

                <Text size="sm" c="dimmed" ta="center">
                  Swipe left = Hard • Swipe right = Easy
                </Text>
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Box>

      {/* Bottom Action Buttons - Always reserve space to prevent card from moving */}
      <Box
        style={{
          backgroundColor: 'white',
          borderTop: '1px solid #e9ecef',
          paddingBottom: isIOS ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
          visibility: isFlipped ? 'visible' : 'hidden',
          opacity: isFlipped ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out',
        }}
      >
        <Box p="md">
          <Group gap="sm" grow>
            <Button
              leftSection={<IconX size={18} />}
              color="red"
              variant="light"
              size="lg"
              onClick={() => handleAnswer(false, 'hard')}
              disabled={!isFlipped}
            >
              Hard
            </Button>
            <Button
              leftSection={<IconCheck size={18} />}
              color="orange"
              variant="light"
              size="lg"
              onClick={() => handleAnswer(true, 'medium')}
              disabled={!isFlipped}
            >
              Good
            </Button>
            <Button
              leftSection={<IconCheck size={18} />}
              color="teal"
              variant="filled"
              size="lg"
              onClick={() => handleAnswer(true, 'easy')}
              disabled={!isFlipped}
            >
              Easy
            </Button>
          </Group>
        </Box>
      </Box>
    </Box>
  );
}
